import { ThaiInspiredLoader } from '@/components/loaders/thai-inspired-loader';
import { PrivateRoute, PublicRoute, VoluntaryInsuranceRoute } from '@/components/route-wrappers';
import vh from '@sect/100vh';
import { lazy, Suspense, useEffect, useEffectEvent, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { tokenLogin } from '../actions/auth';
import { THAI_HOLIDAYS } from '../constants/holidays';
import {
  getAffiliateById,
  getAffiliateProfile as fetchAffiliateProfile,
  getSystemSettings as fetchSystemSettings,
  getUserProfile,
} from '@/modules/auth/shared/api';
import { useAppStore } from '../modules/shared/store/appStore';
import PushNotification from '../PushNotification';
import {
  addClickListeners,
  errorHandler,
  getAccessToken,
  getLocalData,
  getParameterByName,
  unregisterTracking,
  needsRedirection,
  setLocalData,
  setUserData,
  updateTokensFromCookie,
} from '../utils/helper';
import { setAffiliateIdHeader } from '@/modules/auth/shared/hooks/useTokenManager';
import { AuthRoutes } from '../modules/auth';
import LogoutRoute from '../modules/auth/shared/components/LogoutRoute';

const Loading = () => <ThaiInspiredLoader text="Loading..." />;

// The admin landing route. Eagerly imported until now, which put it and its whole
// dependency tree in the entry chunk that every page of the portal has to parse before
// it can render anything. Lazy like the 76 routes below it.
const CreateLeads = lazy(() => import('../modules/create-leads'));
const AllAgents = lazy(() => import('../modules/agents/all-agents'));
const MlmAgents = lazy(() => import('../modules/agents/mlm-agents'));
const CmiSubAgents = lazy(() => import('../modules/agents/cmi-subagents'));
const CmiSubAgentsCommissions = lazy(() => import('../modules/agents/cmi-subagents/commission-rates'));
const Teams = lazy(() => import('../modules/agents/mlm-agents/teams'));
const PlanListing = lazy(() => import('../modules/plan-listing'));
const Profile = lazy(() => import('../modules/profile'));
const ComingSoon = lazy(() => import('../modules/coming-soon'));
const NonMotorPlanListing = lazy(() => import('../modules/non-motor-plan-listing'));
const NonMotorDeclaration = lazy(() => import('../modules/non-motor-plan-listing/declaration'));
const NonMotorPostquote = lazy(() => import('../modules/non-motor-plan-listing/declaration/postquote'));
const NonMotorDetails = lazy(() => import('../modules/non-motor-details'));
const NonMotorPayment = lazy(() => import('../modules/non-motor-plan-listing/declaration/postquote/payment'));
const Crm = lazy(() => import('../modules/crm'));
const CrmAffiliate = lazy(() => import('../modules/agents/agent-detail'));
const StaffForTasks = lazy(() => import('../modules/staff-for-tasks'));
const LeadsModule = lazy(() => import('../modules/leads/index'));
const HolidaysModule = lazy(() => import('../modules/holidays/index'));
const VMIPrequoteForms = lazy(() => import('../modules/prequote-forms/VMI'));
const BikePrequoteForms = lazy(() => import('../modules/prequote-forms/Bike'));
const COAPrequoteForms = lazy(() => import('../modules/prequote-forms/COA'));
const NonMotorPrequoteForm = lazy(() => import('../modules/prequote-forms/NonMotor'));
const CustomQuoteForms = lazy(() => import('../modules/prequote-forms/CustomQuote'));
const BikeCustomQuoteForms = lazy(() => import('../modules/prequote-forms/CustomQuote/Bike'));
const SalereportingForm = lazy(() => import('../modules/plan-listing/sale-reporting'));
const SwitchPackageInformationCorrection = lazy(
  () => import('../modules/plan-listing/sale-reporting/information-correction'),
);
const SwitchPackageReview = lazy(() => import('../modules/plan-listing/sale-reporting/information-correction/review'));
const SendToCustomer = lazy(() => import('../modules/plan-listing/send-to-customer'));
const ComparisonSheet = lazy(() => import('../modules/comparison-sheet'));
const OpsDashboard = lazy(() => import('../modules/ops-dashboard/OpsDashboard'));
const CoverNotePreview = lazy(() => import('../modules/cover-note-preview'));
const LegacyWizardRedirect = lazy(() => import('../modules/legacy-wizard-redirect'));
const DebtCollection = lazy(() => import('../modules/crm/debt-collection'));
const PolicyInProgressWithTabs = lazy(() => import('../modules/leads/policy-in-progress'));
const CompulsoryInsurance = lazy(() => import('../modules/leads/compulsory-insurance'));
const Endorsements = lazy(() => import('../modules/leads/endorsements'));
const InternalInformationUpdate = lazy(() => import('../modules/leads/policy-in-progress/internal-information-update'));
const PremiumUpdate = lazy(() => import('../modules/leads/policy-in-progress/premium-update'));
const CancellationDetails = lazy(
  () => import('../modules/leads/policy-in-progress/cancellation-endorsement/cancellation-details'),
);
const CancellationEndorsementDocuments = lazy(
  () => import('../modules/leads/policy-in-progress/cancellation-endorsement/documents'),
);
const CancellationEndorsementInternalRemarks = lazy(
  () => import('../modules/leads/policy-in-progress/cancellation-endorsement/internal-remarks'),
);
const CancellationEndorsementReview = lazy(
  () => import('../modules/leads/policy-in-progress/cancellation-endorsement/review'),
);
const RecordEndorsementDetails = lazy(
  () => import('../modules/leads/policy-in-progress/record-endorsement/endorsement-details'),
);
const RecordEndorsementInfoCorrection = lazy(
  () => import('../modules/leads/policy-in-progress/record-endorsement/information-correction'),
);
const RecordEndorsementDocuments = lazy(
  () => import('../modules/leads/policy-in-progress/record-endorsement/documents'),
);
const RecordEndorsementInternalRemarks = lazy(
  () => import('../modules/leads/policy-in-progress/record-endorsement/internal-remarks'),
);
const RecordEndorsementReview = lazy(() => import('../modules/leads/policy-in-progress/record-endorsement/review'));
const EndorsementDetails = lazy(() => import('../modules/leads/endorsements/details'));
const EndorsementRedirect = lazy(() => import('../modules/leads/endorsements/redirect'));
const GenerateShortUrl = lazy(() => import('../modules/url-shortener/generate-short-url'));
const BannerUpdate = lazy(() => import('../modules/bannerUpload'));
const NotificationAdmin = lazy(() => import('../modules/notifications'));
const PriceListAdmin = lazy(() => import('../modules/price-list-admin'));
const GamificationAdmin = lazy(() => import('../modules/gamification-admin'));
const ManageStaff = lazy(() => import('../modules/staff-management'));
const PolicyAdmin = lazy(() => import('../modules/policy-admin'));
const PackagePromotion = lazy(() => import('../modules/promotion-admin/package-promotion'));
const CampaignPromotion = lazy(() => import('../modules/promotion-admin/campaign-promotion'));
const InsurerSettings = lazy(() => import('../modules/insurer-settings'));
const IRSettings = lazy(() => import('../modules/ir-settings'));
const CommissionAndPromotions = lazy(() => import('../modules/commission-and-promotions'));
const Downline = lazy(() => import('../modules/agents/mlm-agents/downline'));
const EventCalendar = lazy(() => import('../modules/event-calendar'));
const HistoricalReportRequests = lazy(() => import('../modules/historical-report-requests'));
const ChatwootPayments = lazy(() => import('../modules/chatwoot-payments'));
const LoginForm = lazy(() => import('../modules/auth/login'));
const NonStandardLeads = lazy(() => import('../modules/non-motor-policies/non-standard-leads'));
const NonStandardInProgress = lazy(() => import('../modules/non-motor-policies/non-standard-in-progress'));
const NonMotorLeads = lazy(() => import('../modules/non-motor-policies/non-motor'));
const AssignmentConfig = lazy(() => import('../modules/assignment-config'));
const VoluntaryInsurance = lazy(() => import('../modules/voluntary-insurance'));
const AccountingAgents = lazy(() => import('../modules/accounting/agents'));
const AccountingInsurerTables = lazy(() => import('../modules/accounting/insurer-tables'));
const AccountingInstalments = lazy(() => import('../modules/accounting/instalments'));
const AccountingCredits = lazy(() => import('../modules/accounting/credits'));
const AccountingUploadWht = lazy(() => import('../modules/accounting/upload-wht'));

const Router = () => {
  const [initialLoading, setInitialLoading] = useState(true);
  const [dataFetching, setDataFetching] = useState(false);
  const [user, setUser] = useState<Record<string, any> | null>(null);
  const initRanRef = useRef(false);
  const location = useLocation();

  // Intentionally limited to refetching affiliate profile when leaving affiliate URLs; does not call user profile or system settings (matches legacy behavior).
  const refetchAffiliateProfile = useEffectEvent(() => getAffiliateProfile());
  useEffect(() => {
    window.currentPath = location.pathname + location.search;

    const handleLocationChange = () => {
      useAppStore.getState().setLocation(location as unknown as Record<string, unknown>);

      if (window.currentPath !== '/') {
        window.previousPath = window.currentPath || '/';
      }

      window.currentPath = location.pathname + location.search;

      if (window.previousPath?.includes('affiliate=') && !window.currentPath?.includes('affiliate=')) {
        refetchAffiliateProfile();
      } else if (window.previousPath?.includes('affiliate/') && !window.currentPath?.includes('affiliate=')) {
        refetchAffiliateProfile();
      }
    };

    handleLocationChange();
  }, [location]);

  const bootstrap = useEffectEvent(() => {
    const initialize = async () => {
      const intent = getParameterByName('intent');
      const t = getParameterByName('t');

      if ((intent === 'login' || intent === 'verify_email') && t) {
        await handleTokenLogin(t);
      } else if (intent === 'internal_redirect') {
        // Only on the cross-portal handoff, which is what the cookie is for —
        // thailand-portal appends this param when it redirects here
        updateTokensFromCookie();
        await handleUserProfile();
      } else {
        await handleUserProfile();
      }

      if (!window.previousPath) {
        window.previousPath = window.currentPath || '/';
      }

      useAppStore.getState().setLocation({ pathname: window.location.pathname || '/' });
      addClickListeners();
    };

    vh.init();
    getSystemSettings();

    if (!useAppStore.getState().holidays.length) {
      getHolidays();
    }

    initialize();
  });
  useEffect(() => {
    if (initRanRef.current) {
      return;
    }
    initRanRef.current = true;
    bootstrap();
  }, []);

  /**
   * GA custom dimension the legacy client set from inside `crm.getAffiliateProfile`. Kept at the
   * call site rather than buried in the request: `getAffiliateProfile` is also called by
   * affiliate-select and usePostLogin, which never fired it, and A4 is a deletion — not the moment
   * to start reporting analytics from two more places.
   */
  const setGaAffiliateDimension = (data: { message?: { id?: number; user?: { id?: number } } }) => {
    if (window.ga && data?.message) {
      window.ga('set', 'dimension1', { id: data.message.user?.id, affiliate_id: data.message.id });
    }
  };

  const getSystemSettings = async () => {
    try {
      const resp = await fetchSystemSettings();

      useAppStore.getState().setSystemSettings(resp);
      setTimeout(() => getSystemSettings(), 60000000); // Refresh every ~16 hours
    } catch (error) {
      errorHandler(error);

      // Set default values for critical settings to prevent routing issues
      const defaultSettings = {
        is_sms_vendor_down: false, // Default to SMS working
      };
      useAppStore.getState().setSystemSettings(defaultSettings);

      setTimeout(() => getSystemSettings(), 600000); // Retry in 10 minutes on error
    }
  };

  const getHolidays = async () => {
    const holidays = await THAI_HOLIDAYS();
    useAppStore.getState().setHolidays(holidays);
  };

  const handleTokenLogin = async (t: string) => {
    setDataFetching(true);
    try {
      await tokenLogin(t);
      await handleUserProfile();
    } catch (error) {
      useAppStore.getState().setFirstLoad(true);
      setInitialLoading(false); // Dismiss loading screen on error
    } finally {
      setDataFetching(false);
    }
  };

  const getAffiliateProfile = async () => {
    setDataFetching(true);
    try {
      // `with_affiliate_discounts` was injected by the legacy client for any caller that did not
      // name it; passed explicitly here so the request is unchanged.
      //
      // main's `a933c371c` expressed the same landing-page fix as `ignore_commission_rates: true`
      // on `api.crm.getAffiliateProfile`. That client was deleted at A4b, so the fix is carried
      // instead by `getAffiliateProfile` no longer defaulting `with_commission_rates`.
      const data = await fetchAffiliateProfile({ with_affiliate_discounts: true });

      const affiliate = data.message;
      setGaAffiliateDimension(data);
      useAppStore.getState().setAffiliate(affiliate);
    } catch (error) {
      // Don't dismiss loading screen here as this might be a secondary operation
    } finally {
      setDataFetching(false);
    }
  };

  // The landing page never reads commission_rates, and asking for them makes this blocking call
  // several times slower — main's `a933c371c`. Carried here via `getAffiliateProfile`'s defaults
  // rather than main's `ignore_commission_rates` flag, since the legacy client it used is gone.
  const affiliateProfile = () =>
    fetchAffiliateProfile({ with_affiliate_discounts: true }).then((data) => {
      setGaAffiliateDimension(data);
      return data;
    });

  const affiliateProfileWithId = (id: string | number) => getAffiliateById(id);

  const handleUserProfile = async () => {
    if (window.location.pathname === '/liff-landing') {
      setInitialLoading(false);
      return;
    }

    setDataFetching(true);
    try {
      // api.user.profile took no arguments; the legacy code passed a params
      // object (client_id/client_secret/token) that was silently ignored.
      const profileDetails = await getUserProfile();
      if (window.ga && profileDetails?.id) {
        window.ga('set', 'dimension1', { id: profileDetails.id });
      }

      if (profileDetails) {
        setUserData(profileDetails);

        // Store user roles in localStorage
        if (profileDetails.roles) {
          setLocalData('userData', profileDetails);
        }

        if (profileDetails.roles && profileDetails.roles.includes('admin') && PushNotification) {
          PushNotification.init();
        }

        setUser(profileDetails);

        if (profileDetails.affiliates && profileDetails.affiliates.length) {
          const currentAffiliate = getLocalData('currentAffiliate');

          if (currentAffiliate) {
            setAffiliateIdHeader(currentAffiliate);
          } else if (profileDetails.affiliates.length > 1) {
            const id = profileDetails.affiliates.find(
              (aff: Record<string, any>) => aff.account_type === 'mlm_agent',
            )?.id;
            setAffiliateIdHeader(id);
            setLocalData('currentAffiliate', id);
          } else {
            const id = profileDetails.affiliates[0].id;
            setAffiliateIdHeader(id);
            setLocalData('currentAffiliate', id);
          }
        }
      }

      if (window.location.pathname === '/welcome') {
        setInitialLoading(false);
      } else {
        await getAllData(profileDetails);
        // Loading state will be managed in getAllData
      }
    } catch (error) {
      setInitialLoading(false); // Dismiss loading screen on error
      useAppStore.getState().setFirstLoad(true);
    } finally {
      setDataFetching(false);
    }
  };

  const getAllData = async (profileDetails: Record<string, any>) => {
    setDataFetching(true);

    const promises = [];
    const affiliate = getParameterByName('affiliate');

    if (affiliate && !isNaN(affiliate as unknown as number)) {
      promises.push(affiliateProfileWithId(affiliate));
    } else if (getAccessToken()) {
      promises.push(affiliateProfile());
    }

    try {
      const response = await Promise.all(promises);

      if (response[0]) {
        const affiliate = response[0].message;

        if (needsRedirection(affiliate)) {
          unregisterTracking();
          setInitialLoading(false);
          return;
        }

        useAppStore.getState().setAffiliate(affiliate);
      }

      useAppStore.getState().setFirstLoad(true);
    } catch (error) {
      useAppStore.getState().setFirstLoad(true);
    } finally {
      // Only set initialLoading to false here, after ALL data is loaded
      setInitialLoading(false);
      setDataFetching(false);
    }
  };

  return (
    <>
      {initialLoading ? (
        <ThaiInspiredLoader text="Loading..." />
      ) : (
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route
              path="/agents/all-agents"
              element={
                <PrivateRoute>
                  <AllAgents />
                </PrivateRoute>
              }
            />
            <Route
              path="/agents/mlm-agents"
              element={
                <PrivateRoute>
                  <MlmAgents />
                </PrivateRoute>
              }
            />
            <Route
              path="/teams"
              element={
                <PrivateRoute>
                  <Teams />
                </PrivateRoute>
              }
            />
            <Route
              path="/agents/cmi-subagents"
              element={
                <PrivateRoute>
                  <CmiSubAgents />
                </PrivateRoute>
              }
            />
            <Route
              path="/agents/cmi-subagents/commissions"
              element={
                <PrivateRoute>
                  <CmiSubAgentsCommissions />
                </PrivateRoute>
              }
            />
            <Route
              path="/plan-listing"
              element={
                <PrivateRoute>
                  <PlanListing />
                </PrivateRoute>
              }
            />
            <Route
              path="/comparison-sheet"
              element={
                <PrivateRoute>
                  <ComparisonSheet />
                </PrivateRoute>
              }
            />
            <Route
              path="/cover-note-preview"
              element={
                <PrivateRoute>
                  <CoverNotePreview />
                </PrivateRoute>
              }
            />
            <Route
              path="/ops-dashboard"
              element={
                <PrivateRoute>
                  <OpsDashboard embedded hostOwnsChrome />
                </PrivateRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              }
            />
            <Route
              path="/buy-insurance"
              element={
                <PrivateRoute>
                  <ComingSoon />
                </PrivateRoute>
              }
            />
            <Route
              path="/policy-in-progress/:tab?"
              element={
                <PrivateRoute>
                  <PolicyInProgressWithTabs />
                </PrivateRoute>
              }
            />

            <Route
              path="/internal-information-update"
              element={
                <PrivateRoute>
                  <InternalInformationUpdate />
                </PrivateRoute>
              }
            />
            <Route
              path="/premium-update"
              element={
                <PrivateRoute>
                  <PremiumUpdate />
                </PrivateRoute>
              }
            />
            <Route
              path="/cancellation-endorsement/cancellation-details"
              element={
                <PrivateRoute>
                  <CancellationDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/cancellation-endorsement/documents"
              element={
                <PrivateRoute>
                  <CancellationEndorsementDocuments />
                </PrivateRoute>
              }
            />
            <Route
              path="/cancellation-endorsement/internal-remarks"
              element={
                <PrivateRoute>
                  <CancellationEndorsementInternalRemarks />
                </PrivateRoute>
              }
            />
            <Route
              path="/cancellation-endorsement/review"
              element={
                <PrivateRoute>
                  <CancellationEndorsementReview />
                </PrivateRoute>
              }
            />
            <Route
              path="/record-endorsement/endorsement-details"
              element={
                <PrivateRoute>
                  <RecordEndorsementDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/record-endorsement/information-correction"
              element={
                <PrivateRoute>
                  <RecordEndorsementInfoCorrection />
                </PrivateRoute>
              }
            />
            <Route
              path="/record-endorsement/documents"
              element={
                <PrivateRoute>
                  <RecordEndorsementDocuments />
                </PrivateRoute>
              }
            />
            <Route
              path="/record-endorsement/internal-remarks"
              element={
                <PrivateRoute>
                  <RecordEndorsementInternalRemarks />
                </PrivateRoute>
              }
            />
            <Route
              path="/record-endorsement/review"
              element={
                <PrivateRoute>
                  <RecordEndorsementReview />
                </PrivateRoute>
              }
            />

            <Route
              path="/endorsement-details/:id"
              element={
                <PrivateRoute>
                  <EndorsementDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/endorsements/:id"
              element={
                <PrivateRoute>
                  <EndorsementRedirect />
                </PrivateRoute>
              }
            />
            <Route
              path="/compulsory-insurance"
              element={
                <PrivateRoute>
                  <CompulsoryInsurance />
                </PrivateRoute>
              }
            />
            <Route
              path="/non-motor-plan-listing"
              element={
                <PrivateRoute>
                  <NonMotorPlanListing />
                </PrivateRoute>
              }
            />
            <Route
              path="/non-motor-declaration"
              element={
                <PrivateRoute>
                  <NonMotorDeclaration />
                </PrivateRoute>
              }
            />
            <Route
              path="/non-motor-postquote"
              element={
                <PrivateRoute>
                  <NonMotorPostquote />
                </PrivateRoute>
              }
            />
            <Route
              path="/non-motor-payment"
              element={
                <PrivateRoute>
                  <NonMotorPayment />
                </PrivateRoute>
              }
            />
            <Route
              path="/non-motor-details/:id"
              element={
                <PrivateRoute>
                  <NonMotorDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/crm"
              element={
                <PrivateRoute>
                  <Crm />
                </PrivateRoute>
              }
            />
            <Route
              path="/crm/affiliate/:id"
              element={
                <PrivateRoute>
                  <CrmAffiliate />
                </PrivateRoute>
              }
            />
            <Route
              path="/crm/debt-collection"
              element={
                <PrivateRoute>
                  <DebtCollection />
                </PrivateRoute>
              }
            />
            <Route
              path="/staff-for-tasks"
              element={
                <PrivateRoute>
                  <StaffForTasks />
                </PrivateRoute>
              }
            />
            <Route
              path="/leads"
              element={
                <PrivateRoute>
                  <Navigate to="/leads/new-leads" replace />
                </PrivateRoute>
              }
            />
            <Route
              path="/leads/:type"
              element={
                <PrivateRoute>
                  <LeadsModule />
                </PrivateRoute>
              }
            />
            <Route
              path="/get-quote/voluntary-insurance"
              element={
                <PrivateRoute>
                  <VMIPrequoteForms />
                </PrivateRoute>
              }
            />
            <Route
              path="/get-quote/bike-insurance"
              element={
                <PrivateRoute>
                  <BikePrequoteForms />
                </PrivateRoute>
              }
            />
            <Route
              path="/get-quote/non-motor"
              element={
                <VoluntaryInsuranceRoute>
                  <NonMotorPrequoteForm />
                </VoluntaryInsuranceRoute>
              }
            />
            <Route
              path="/get-quote/change-of-agent"
              element={
                <PrivateRoute>
                  <COAPrequoteForms />
                </PrivateRoute>
              }
            />
            <Route
              path="/get-quote/custom-quote"
              element={
                <PrivateRoute>
                  <CustomQuoteForms />
                </PrivateRoute>
              }
            />
            <Route
              path="/get-quote/bike-custom-quote"
              element={
                <PrivateRoute>
                  <BikeCustomQuoteForms />
                </PrivateRoute>
              }
            />
            <Route
              path="/sale-reporting"
              element={
                <PrivateRoute>
                  <SalereportingForm />
                </PrivateRoute>
              }
            />
            <Route
              path="/switch-package/information-correction"
              element={
                <PrivateRoute>
                  <SwitchPackageInformationCorrection />
                </PrivateRoute>
              }
            />
            <Route
              path="/switch-package/review"
              element={
                <PrivateRoute>
                  <SwitchPackageReview />
                </PrivateRoute>
              }
            />
            <Route
              path="/send-to-customer"
              element={
                <PrivateRoute>
                  <SendToCustomer />
                </PrivateRoute>
              }
            />

            <Route
              path="/shortened-url"
              element={
                <PrivateRoute>
                  <GenerateShortUrl />
                </PrivateRoute>
              }
            />
            <Route
              path="/banner-upload"
              element={
                <PrivateRoute>
                  <BannerUpdate />
                </PrivateRoute>
              }
            />
            <Route
              path="/notification-admin"
              element={
                <PrivateRoute>
                  <NotificationAdmin />
                </PrivateRoute>
              }
            />
            <Route
              path="/price-list-admin"
              element={
                <PrivateRoute>
                  <Navigate to="/price-list-admin/modify" replace />
                </PrivateRoute>
              }
            />
            <Route
              path="/price-list-admin/:type"
              element={
                <PrivateRoute>
                  <PriceListAdmin />
                </PrivateRoute>
              }
            />
            <Route
              path="/gamification-admin"
              element={
                <PrivateRoute>
                  <GamificationAdmin />
                </PrivateRoute>
              }
            />
            <Route
              path="/policy-admin"
              element={
                <PrivateRoute>
                  <PolicyAdmin />
                </PrivateRoute>
              }
            />
            <Route
              path="/promotion-admin"
              element={
                <PrivateRoute>
                  <Navigate to="/promotion-admin/package-promotion" replace />
                </PrivateRoute>
              }
            />
            <Route
              path="/promotion-admin/package-promotion"
              element={
                <PrivateRoute>
                  <PackagePromotion />
                </PrivateRoute>
              }
            />
            <Route
              path="/promotion-admin/campaign-promotion"
              element={
                <PrivateRoute>
                  <Navigate to="/promotion-admin/campaign-promotion/upload" replace />
                </PrivateRoute>
              }
            />
            <Route
              path="/promotion-admin/campaign-promotion/:type"
              element={
                <PrivateRoute>
                  <CampaignPromotion />
                </PrivateRoute>
              }
            />
            <Route
              path="/manage-staff"
              element={
                <PrivateRoute>
                  <ManageStaff />
                </PrivateRoute>
              }
            />
            <Route
              index
              element={
                <PublicRoute>
                  <LoginForm />
                </PublicRoute>
              }
            />
            {/* Legacy `/fairdee-wizard?id=` links already sent out — forwards to the
                OPS dashboard by resolving the quotation query id (FTT-16267). */}
            <Route
              path="/fairdee-wizard"
              element={
                <PrivateRoute>
                  <LegacyWizardRedirect />
                </PrivateRoute>
              }
            />
            <Route
              path="/create-leads"
              element={
                <PrivateRoute>
                  <CreateLeads />
                </PrivateRoute>
              }
            />
            <Route
              path="/insurer-settings"
              element={
                <PrivateRoute>
                  <Navigate to="/insurer-settings/insurer-email" replace />
                </PrivateRoute>
              }
            />
            <Route
              path="/insurer-settings/:type"
              element={
                <PrivateRoute>
                  <InsurerSettings />
                </PrivateRoute>
              }
            />
            <Route
              path="/ir-settings"
              element={
                <PrivateRoute>
                  <Navigate to="/ir-settings/pre-quote-sla" replace />
                </PrivateRoute>
              }
            />
            <Route
              path="/ir-settings/:type"
              element={
                <PrivateRoute>
                  <IRSettings />
                </PrivateRoute>
              }
            />
            <Route
              path="/commission-and-promotions"
              element={
                <PrivateRoute>
                  <Navigate to="/commission-and-promotions/agent-transfers" replace />
                </PrivateRoute>
              }
            />
            <Route
              path="/commission-and-promotions/:type"
              element={
                <PrivateRoute>
                  <CommissionAndPromotions />
                </PrivateRoute>
              }
            />
            <Route
              path="/downline"
              element={
                <PrivateRoute>
                  <Downline />
                </PrivateRoute>
              }
            />
            <Route
              path="/historical-report-requests"
              element={
                <PrivateRoute>
                  <HistoricalReportRequests />
                </PrivateRoute>
              }
            />
            <Route
              path="/accounting/agents"
              element={
                <PrivateRoute>
                  <Navigate to="/accounting/agents/pending-payments" replace />
                </PrivateRoute>
              }
            />
            <Route
              path="/accounting/agents/:type"
              element={
                <PrivateRoute>
                  <AccountingAgents />
                </PrivateRoute>
              }
            />
            <Route
              path="/accounting/insurer-tables"
              element={
                <PrivateRoute>
                  <Navigate to="/accounting/insurer-tables/soa-v1" replace />
                </PrivateRoute>
              }
            />
            <Route
              path="/accounting/insurer-tables/:type"
              element={
                <PrivateRoute>
                  <AccountingInsurerTables />
                </PrivateRoute>
              }
            />
            <Route
              path="/accounting/instalments"
              element={
                <PrivateRoute>
                  <Navigate to="/accounting/instalments/manual-ekyc-transfer" replace />
                </PrivateRoute>
              }
            />
            <Route
              path="/accounting/instalments/:type"
              element={
                <PrivateRoute>
                  <AccountingInstalments />
                </PrivateRoute>
              }
            />
            <Route
              path="/accounting/credits"
              element={
                <PrivateRoute>
                  <AccountingCredits />
                </PrivateRoute>
              }
            />
            <Route
              path="/accounting/upload-wht"
              element={
                <PrivateRoute>
                  <AccountingUploadWht />
                </PrivateRoute>
              }
            />
            <Route
              path="/holidays"
              element={
                <PrivateRoute>
                  <HolidaysModule />
                </PrivateRoute>
              }
            />
            <Route
              path="/event-calendar"
              element={
                <PrivateRoute>
                  <EventCalendar />
                </PrivateRoute>
              }
            />
            <Route
              path="/chatwoot-payments"
              element={
                <PrivateRoute>
                  <ChatwootPayments />
                </PrivateRoute>
              }
            />
            <Route
              path="/assignment-config"
              element={
                <PrivateRoute>
                  <AssignmentConfig />
                </PrivateRoute>
              }
            />
            <Route
              path="/voluntary-insurance/:tab?/:subTab?"
              element={
                <PrivateRoute>
                  <VoluntaryInsurance />
                </PrivateRoute>
              }
            />
            <Route
              path="/endorsement"
              element={
                <PrivateRoute>
                  <Endorsements />
                </PrivateRoute>
              }
            />
            <Route
              path="/non-standard-leads"
              element={
                <PrivateRoute>
                  <NonStandardLeads />
                </PrivateRoute>
              }
            />
            <Route
              path="/non-standard-in-progress"
              element={
                <PrivateRoute>
                  <NonStandardInProgress />
                </PrivateRoute>
              }
            />
            <Route
              path="/non-motor-leads"
              element={
                <PrivateRoute>
                  <NonMotorLeads />
                </PrivateRoute>
              }
            />
            <Route path="/logout" element={<LogoutRoute />} />
            {AuthRoutes()}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      )}
    </>
  );
};

export default Router;
